import { MailOutlined, MobileOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Radio, theme, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { chooseMFAEmail, postponeMFA } from '@/api/mfa';
import { isAuthErrorResponse } from '@/api/user';
import SharedLoginSunsetNotice from '@/components/auth/SharedLoginSunsetNotice';
import { useApiMutation } from '@/hooks/api';
import { SPACE_MD, SPACE_SM } from '@/theme/buildAntdTheme';
import FormErrorAlert from '@/UI/FormErrorAlert';
import { SubsectionHeading } from '@/UI/typography';

import type { AuthTokenResponse } from '@/api/user';
import type { ReactNode } from 'react';

type Method = 'email' | 'app';

interface MFAMethodSelectionProps {
  mfaToken: string;
  userEmail: string;
  canPostpone?: boolean;
  onEmailChosen: (challenge: AuthTokenResponse) => void;
  onAppChosen: () => void;
  onPostponeSuccess?: (loginData: AuthTokenResponse) => void;
  onBack: () => void;
}

/** First MFA login step for users with no stored preference: email OTP or authenticator app. */
const MFAMethodSelection = ({
  mfaToken,
  userEmail,
  canPostpone = false,
  onEmailChosen,
  onAppChosen,
  onPostponeSuccess,
  onBack,
}: MFAMethodSelectionProps) => {
  const [t] = useTranslation();
  const { token } = theme.useToken();
  const [selected, setSelected] = useState<Method>('email');
  const [error, setError] = useState('');

  const chooseEmailMutation = useApiMutation({
    mutationFn: () => chooseMFAEmail(mfaToken),
    errorKey: 'MFA_METHOD_ERROR',
    onSuccess: (result) => {
      if (!result || isAuthErrorResponse(result)) {
        setError(t('MFA_METHOD_ERROR'));
        return;
      }
      onEmailChosen(result);
    },
  });

  const postponeMutation = useApiMutation({
    mutationFn: () => postponeMFA(mfaToken),
    errorKey: 'MFA_METHOD_ERROR',
    onSuccess: (result) => {
      if (!result || isAuthErrorResponse(result)) {
        setError(t('MFA_METHOD_ERROR'));
        return;
      }
      onPostponeSuccess?.(result);
    },
  });

  const busy = chooseEmailMutation.loading || postponeMutation.loading;

  const handleContinue = () => {
    setError('');
    if (selected === 'app') {
      onAppChosen();
      return;
    }
    chooseEmailMutation.mutate();
  };

  const options: {
    value: Method;
    icon: ReactNode;
    title: string;
    subtitle: string;
  }[] = [
    {
      value: 'email',
      icon: <MailOutlined />,
      title: t('MFA_METHOD_EMAIL_TITLE'),
      subtitle: t('MFA_OPTION_EMAIL_SUB', { email: userEmail }),
    },
    {
      value: 'app',
      icon: <MobileOutlined />,
      title: t('MFA_OPTION_APP_TITLE'),
      subtitle: t('MFA_OPTION_APP_SUB'),
    },
  ];

  return (
    <Flex vertical gap={SPACE_MD} data-testid="mfa-method-selection">
      <SubsectionHeading>{t('MFA_SELECTION_TITLE')}</SubsectionHeading>
      {canPostpone ? <SharedLoginSunsetNotice /> : null}
      <Typography.Text type="secondary">
        {t('MFA_SELECTION_DESCRIPTION')}
      </Typography.Text>

      <FormErrorAlert
        error={error || chooseEmailMutation.error || postponeMutation.error}
        fallbackKey="MFA_METHOD_ERROR"
      />

      <Radio.Group
        value={selected}
        onChange={(event) => setSelected(event.target.value as Method)}
      >
        <Flex vertical gap={SPACE_SM}>
          {options.map(({ value, icon, title, subtitle }) => (
            <Card
              key={value}
              hoverable
              size="small"
              onClick={() => setSelected(value)}
              style={
                selected === value
                  ? { borderColor: token.colorPrimary }
                  : undefined
              }
            >
              <Radio value={value}>
                <Flex gap={SPACE_SM} align="flex-start">
                  <Typography.Text type="secondary">{icon}</Typography.Text>
                  <Flex vertical>
                    <Typography.Text strong>{title}</Typography.Text>
                    <Typography.Text type="secondary">
                      {subtitle}
                    </Typography.Text>
                  </Flex>
                </Flex>
              </Radio>
            </Card>
          ))}
        </Flex>
      </Radio.Group>

      <Typography.Text type="secondary">
        {t('MFA_SELECTION_SETTINGS_HINT')}
      </Typography.Text>

      <Button
        type="primary"
        size="large"
        block
        loading={chooseEmailMutation.loading}
        disabled={busy}
        onClick={handleContinue}
      >
        {t('CONTINUE')}
      </Button>

      {canPostpone ? (
        <Typography.Link
          data-testid="mfa-postpone"
          disabled={busy}
          onClick={() => {
            setError('');
            postponeMutation.mutate();
          }}
        >
          {postponeMutation.loading
            ? t('MFA_POSTPONE_LOADING')
            : t('MFA_POSTPONE_CTA')}
        </Typography.Link>
      ) : null}

      <Typography.Link onClick={onBack}>
        ← {t('MFA_BACK_TO_LOGIN')}
      </Typography.Link>
    </Flex>
  );
};

export default MFAMethodSelection;
